import type { Post } from '@/stores/auth-store'
import {
  getCanonicalServiceVariantLabel,
  getOrganizationTreeSetupLabel,
  isOrganizationServiceVariant,
  readServiceVariantIdentity,
} from '@/lib/service-variant-types'

type ExtraAppCapability =
  | 'can_access_projects'
  | 'can_access_job_projects'
  | 'can_manage_projects'
  | 'can_create_projects'
  | 'can_access_events'
  | 'can_manage_events'
  | 'can_create_events'
  | 'can_access_membership'
  | 'can_manage_in_apps'
  | 'can_access_worker_tracking'
  | 'can_access_job_categories'
  | 'can_access_job_audit'
  | 'can_manage_credentials'
  | 'can_manage_templates'
  | 'can_manage_communication_chat'
  | 'can_manage_communication_email'
  | 'can_manage_communication_sms'
  | 'can_manage_communication_whatsapp'
  | 'can_manage_communication_slack'
  | 'can_manage_roles'
  | 'can_manage_features'
  | 'can_access_worker_attendance'
  | 'can_access_expense_categories'
  | 'can_access_expense_payments'
  | 'can_manage_membership_payments'
  | 'can_manage_membership_plans'
  | 'can_manage_membership_subscriptions'
  | 'can_view_vendor_directory'
  | 'can_view_business_customer_directory'
  | 'can_view_retail_customer_directory'
  | 'can_access_announcements'
  | 'can_create_announcements'
  | 'can_update_announcements'
  | 'can_delete_announcements'
  | 'can_access_assets'
  | 'can_access_maintenance_board'

type AppCapability = keyof NonNullable<NonNullable<Post['capabilities']>> | ExtraAppCapability
type RouteScope = 'shared' | 'organization' | 'service'

const STRICT_CAPABILITY_GUARDS = String(
  import.meta.env.VITE_STRICT_CAPABILITY_GUARDS ?? '',
).trim().toLowerCase() === 'true'

const MEMBERSHIP_FEATURE_ENABLED = String(
  import.meta.env.MEMBERSHIP
  ?? import.meta.env.VITE_MEMBERSHIP
  ?? '',
).trim().length > 0

const PROJECT_MGMT_FEATURE_ENABLED = String(
  import.meta.env.PROJECT_MGMT
  ?? import.meta.env.VITE_PROJECT_MGMT
  ?? '',
).trim().length > 0

const EVENT_MGMT_FEATURE_ENABLED = String(
  import.meta.env.EVENT_MGMT
  ?? import.meta.env.VITE_EVENT_MGMT
  ?? '',
).trim().length > 0

const SYSTEM_ORG_ADMIN_ROLE_UNIQUE_IDS = new Set(['r00torg0', 'sup3rorg'])

const PRIMARY_SERVICE_WORKSPACE_ADMIN_ROLE_UNIQUE_IDS = new Set([
  'bradm1400', // Service Admin (Services / 1400)
  'bradm101',
  'bradm200',
  'bradm300',
  'bradm400',
  'bradm500',
  'bradm600',
  'bradm700',
  'bradm800',
  'bradm900',
  'bradm1000',
  'bradm1200',
  'bradm1300',
  'artsadm',
])

const FINANCE_SECTION_CAPABILITIES = new Set<AppCapability>([
  'can_access_finance',
  'can_access_finance_reports',
  'can_access_monthwise_reports',
  'can_access_company_reports',
  'can_access_worker_reports',
  'can_access_client_reports',
  'can_access_expenses',
  'can_access_invoice_management',
  'can_access_invoices',
  'can_access_invoice_payments',
  'can_access_inventory',
  'can_access_worker_invoices',
  'can_access_worker_payouts',
  'can_access_company_payments',
  'can_access_inter_company_settlement',
])

const CUSTOMER_DIRECTORY_SECTION_CAPABILITIES = new Set<AppCapability>([
  'can_view_customer_directory',
  'can_view_retail_customer_directory',
  'can_view_business_customer_directory',
])

const JOB_MANAGEMENT_SECTION_CAPABILITIES = new Set<AppCapability>([
  'can_access_job_management',
  'can_access_jobs',
  'can_access_job_categories',
  'can_access_job_audit',
  'can_access_job_customers',
  'can_access_workers',
  'can_access_vehicles',
  'can_access_assets',
  'can_access_maintenance_board',
  'can_access_job_locations',
  'can_access_assignments',
  'can_access_worker_skills',
  'can_access_teams',
  'can_access_workdays',
  'can_access_job_configuration',
  'can_access_job_workflows',
  'can_access_job_status_transitions',
  'can_access_group_workday_access',
])

const COMMUNICATION_SECTION_CAPABILITIES = new Set<AppCapability>([
  'can_access_communication',
  'can_view_communication',
])

const WORKER_CHILD_CAPABILITIES = new Set<AppCapability>([
  'can_access_worker_hotline',
  'can_access_worker_tracking',
])

const FEATURE_SECTION_PARENT_GATES: Array<{ parent: AppCapability; section: Set<AppCapability> }> = [
  { parent: 'can_access_finance', section: FINANCE_SECTION_CAPABILITIES },
  { parent: 'can_view_customer_directory', section: CUSTOMER_DIRECTORY_SECTION_CAPABILITIES },
  { parent: 'can_access_job_management', section: JOB_MANAGEMENT_SECTION_CAPABILITIES },
  { parent: 'can_access_communication', section: COMMUNICATION_SECTION_CAPABILITIES },
  { parent: 'can_access_workers', section: WORKER_CHILD_CAPABILITIES },
]

const isCapabilityBlockedBySectionParent = (
  capability: AppCapability,
  capabilities: Partial<Record<AppCapability, boolean>> | null | undefined,
) => FEATURE_SECTION_PARENT_GATES.some(({ parent, section }) => (
  section.has(capability) && capabilities?.[parent] === false
))

const SERVICE_WORKSPACE_ADMIN_FALLBACK_CAPABILITIES: AppCapability[] = [
  'can_view_branch_directory',
  'can_access_branch_management',
  'can_view_user_directory',
  'can_access_user_directory',
  'can_create_users',
  'can_update_users',
  'can_approve_users',
  'can_delete_users',
  'can_manage_user_lifecycle',
  'can_unlock_users',
  'can_force_password_reset_users',
  'can_assign_user_roles',
  'can_remove_user_roles',
  'can_view_role_directory',
  'can_list_role_assignments',
  'can_manage_roles',
  'can_use_maps',
  'can_access_job_management',
  'can_access_jobs',
  'can_access_job_categories',
  'can_access_job_audit',
  'can_access_projects',
  'can_manage_projects',
  'can_access_job_customers',
  'can_access_workers',
  'can_access_vehicles',
  'can_access_assets',
  'can_access_maintenance_board',
  'can_access_job_locations',
  'can_access_devices',
  'can_access_assignments',
  'can_access_worker_skills',
  'can_access_teams',
  'can_access_workdays',
  'can_access_job_configuration',
  'can_access_job_workflows',
  'can_access_job_status_transitions',
  'can_access_finance',
  'can_access_finance_reports',
  'can_access_monthwise_reports',
  'can_access_company_reports',
  'can_access_worker_reports',
  'can_access_client_reports',
  'can_access_expenses',
  'can_access_invoice_management',
  'can_access_invoices',
  'can_access_invoice_payments',
  'can_access_inventory',
  'can_access_worker_invoices',
  'can_access_worker_payouts',
  'can_access_company_payments',
  'can_access_inter_company_settlement',
  'can_view_customer_directory',
  'can_access_communication',
  'can_view_communication',
  'can_access_branch_service_products',
  'can_access_service_orders',
  'can_access_service_offers',
  'can_access_resource_planner',
  'can_access_service_categories',
  'can_access_service_pos',
  'can_access_product_variants',
  'can_access_announcements',
  'can_manage_credentials',
  'can_manage_templates',
  'can_manage_communication_chat',
  'can_manage_communication_email',
  'can_manage_communication_sms',
  'can_manage_communication_whatsapp',
  'can_manage_communication_slack',
  'can_manage_roles',
  'can_manage_features',
  'can_access_worker_attendance',
  'can_access_expense_categories',
  'can_access_expense_payments',
  'can_manage_membership_payments',
  'can_manage_membership_plans',
  'can_manage_membership_subscriptions',
  'can_view_vendor_directory',
  'can_view_business_customer_directory',
  'can_view_retail_customer_directory',
]

const SYSTEM_ORG_ADMIN_FALLBACK_CAPABILITIES: AppCapability[] = [
  'can_view_business_dashboard',
  'can_view_organization_tree',
  'can_access_organization_workspace',
  'can_view_branch_directory',
  'can_access_branch_management',
  'can_view_tenant_directory',
  'can_access_tenant_directory',
  'can_access_organization_verticals',
  'can_view_user_directory',
  'can_access_user_directory',
  'can_create_users',
  'can_update_users',
  'can_approve_users',
  'can_delete_users',
  'can_manage_user_lifecycle',
  'can_unlock_users',
  'can_force_password_reset_users',
  'can_assign_user_roles',
  'can_remove_user_roles',
  'can_view_role_directory',
  'can_list_role_assignments',
  'can_manage_roles',
  'can_access_templates',
  'can_use_maps',
  'can_access_job_management',
  'can_access_jobs',
  'can_access_job_categories',
  'can_access_job_audit',
  'can_access_projects',
  'can_manage_projects',
  'can_access_job_customers',
  'can_access_workers',
  'can_access_vehicles',
  'can_access_assets',
  'can_access_maintenance_board',
  'can_access_job_locations',
  'can_access_devices',
  'can_access_assignments',
  'can_access_worker_skills',
  'can_access_teams',
  'can_access_workdays',
  'can_access_job_configuration',
  'can_access_job_workflows',
  'can_access_job_status_transitions',
  'can_access_finance',
  'can_access_finance_reports',
  'can_access_monthwise_reports',
  'can_access_company_reports',
  'can_access_worker_reports',
  'can_access_client_reports',
  'can_access_expenses',
  'can_access_invoice_management',
  'can_access_invoices',
  'can_access_invoice_payments',
  'can_access_inventory',
  'can_access_worker_invoices',
  'can_access_worker_payouts',
  'can_access_company_payments',
  'can_access_inter_company_settlement',
  'can_view_customer_directory',
  'can_access_communication',
  'can_view_communication',
  'can_access_announcements',
  'can_manage_credentials',
  'can_manage_templates',
  'can_manage_communication_chat',
  'can_manage_communication_email',
  'can_manage_communication_sms',
  'can_manage_communication_whatsapp',
  'can_manage_communication_slack',
  'can_manage_roles',
  'can_manage_features',
  'can_access_worker_attendance',
  'can_access_expense_categories',
  'can_access_expense_payments',
  'can_manage_membership_payments',
  'can_manage_membership_plans',
  'can_manage_membership_subscriptions',
  'can_view_vendor_directory',
  'can_view_business_customer_directory',
  'can_view_retail_customer_directory',
]

const BRANCH_MEDIA_PERMISSION_ACTIONS = [
  'list_service_instance_media',
  'add_service_instance_media',
  'remove_service_instance_media_asset',
]

type RouteAccessDefinition = {
  path: string
  feature: string
  allowedScopes: RouteScope[]
  anyCapabilities?: AppCapability[]
}

export type RouteAccessDecision = {
  authorized: boolean
  feature: string | null
  requiredScopeLabel: string | null
  reason: 'scope' | 'capability' | null
}

export const CUSTOMER_DIRECTORY_CAPABILITIES: AppCapability[] = [
  'can_view_customer_directory',
  'can_view_retail_customer_directory',
  'can_view_business_customer_directory',
]

export const USER_DIRECTORY_CAPABILITIES: AppCapability[] = [
  'can_view_user_directory',
  'can_view_customer_directory',
  'can_view_retail_customer_directory',
  'can_view_business_customer_directory',
  'can_view_client_directory',
  'can_view_vendor_directory',
  'can_create_vendors',
  'can_update_vendors',
  'can_remove_vendors',
  'can_view_supplier_directory',
  'can_create_suppliers',
  'can_update_suppliers',
  'can_remove_suppliers',
  'can_access_user_directory',
  'can_create_users',
  'can_update_users',
  'can_approve_users',
  'can_delete_users',
  'can_manage_user_lifecycle',
  'can_unlock_users',
  'can_assign_user_roles',
  'can_remove_user_roles',
]

export function isSystemOrgAdminRole(post: Post | null | undefined): boolean {
  const roleUniqueId = String(post?.roles_data?.unique_id ?? post?.role_id ?? '').trim().toLowerCase()
  return SYSTEM_ORG_ADMIN_ROLE_UNIQUE_IDS.has(roleUniqueId)
}

export function isPrimaryServiceWorkspaceAdminRole(post: Post | null | undefined): boolean {
  const roleUniqueId = String(post?.roles_data?.unique_id ?? '').trim().toLowerCase()
  if (PRIMARY_SERVICE_WORKSPACE_ADMIN_ROLE_UNIQUE_IDS.has(roleUniqueId)) {
    return true
  }

  if (roleUniqueId.startsWith('bradm') || roleUniqueId === 'artsadm') {
    return true
  }

  const roleName = String(post?.roles_data?.roles_name ?? post?.role_name ?? '').trim().toLowerCase()
  return roleName === 'service admin'
    || roleName === 'branch admin'
}

type CapabilityFallbackOptions = {
  excludeCapabilities?: AppCapability[]
}

export function withSystemRoleCapabilityFallback(
  post: Post | null | undefined,
  capabilities: Partial<Record<AppCapability, boolean>> | null | undefined,
  options?: CapabilityFallbackOptions,
): Partial<Record<AppCapability, boolean>> | undefined {
  if (STRICT_CAPABILITY_GUARDS) {
    return capabilities ?? undefined
  }

  const fallbackCapabilities = isSystemOrgAdminRole(post)
    ? SYSTEM_ORG_ADMIN_FALLBACK_CAPABILITIES
    : isPrimaryServiceWorkspaceAdminRole(post)
      ? SERVICE_WORKSPACE_ADMIN_FALLBACK_CAPABILITIES
      : null

  if (!fallbackCapabilities) {
    return capabilities ?? undefined
  }

  const excluded = new Set(options?.excludeCapabilities ?? [])
  const next: Partial<Record<AppCapability, boolean>> = {
    ...(capabilities ?? {}),
  }

  for (const capability of fallbackCapabilities) {
    if (excluded.has(capability)) {
      continue
    }
    if (isCapabilityBlockedBySectionParent(capability, next)) {
      continue
    }
    if (next.can_access_worker_hotline === false && capability === 'can_access_worker_hotline') {
      continue
    }
    if (next.can_view_customer_directory === false && capability === 'can_access_job_customers') {
      continue
    }
    if (next[capability] === false) {
      continue
    }
    next[capability] = true
  }

  return next
}

export const ROLE_DIRECTORY_CAPABILITIES: AppCapability[] = [
  'can_view_role_directory',
  'can_list_role_assignments',
  'can_manage_roles',
]

export const VENDOR_CAPABILITIES: AppCapability[] = [
  'can_view_vendor_directory',
  'can_create_vendors',
  'can_update_vendors',
  'can_remove_vendors',
]

export const SUPPLIER_CAPABILITIES: AppCapability[] = [
  'can_view_supplier_directory',
  'can_create_suppliers',
  'can_update_suppliers',
  'can_remove_suppliers',
]

export const BRANCH_CAPABILITIES: AppCapability[] = [
  'can_view_branch_directory',
  'can_access_branch_management',
  'can_create_branches',
  'can_change_branch_admin',
  'can_manage_branch_state',
  'can_remove_branches',
]

const TEMPLATES_CAPABILITIES = ['can_access_templates'] as AppCapability[]

/** Services variant (store section): sidebar maps to Features under service_id=5 + variant 13. */
const SERVICE_STORE_POS_CAPABILITIES = ['can_access_service_pos'] as AppCapability[]
const SERVICE_STORE_PRODUCTS_CAPABILITIES = ['can_access_branch_service_products'] as AppCapability[]
const SERVICE_STORE_CATEGORIES_CAPABILITIES = ['can_access_service_categories'] as AppCapability[]
export const DEVICES_CAPABILITIES = ['can_access_devices'] as AppCapability[]
const SERVICE_STORE_OFFERS_CAPABILITIES = ['can_access_service_offers'] as AppCapability[]
const SERVICE_STORE_ORDERS_CAPABILITIES = ['can_access_service_orders'] as AppCapability[]
const RESOURCE_PLANNER_CAPABILITIES = ['can_access_resource_planner'] as AppCapability[]
const MEMBERSHIP_CAPABILITIES = ['can_access_membership'] as AppCapability[]
const IN_APPS_CAPABILITIES = ['can_manage_in_apps'] as AppCapability[]
const ANNOUNCEMENTS_CAPABILITIES = ['can_access_announcements'] as AppCapability[]
const SERVICE_DASHBOARD_CAPABILITIES = ['can_view_business_dashboard'] as AppCapability[]
const COMMUNICATION_CAPABILITIES = [
  'can_access_communication',
  'can_view_communication',
  'can_manage_communication_providers',
  'can_manage_communication_routing',
  'can_test_communication',
  'can_manage_whatsapp_communication',
  ...SERVICE_STORE_ORDERS_CAPABILITIES,
] as AppCapability[]

const JOB_MANAGEMENT_CAPABILITIES = ['can_access_job_management'] as AppCapability[]
const JOBS_CAPABILITIES = ['can_access_jobs'] as AppCapability[]
const JOB_CATEGORIES_CAPABILITIES = ['can_access_job_categories'] as AppCapability[]
const PROJECTS_CAPABILITIES = ['can_access_projects', 'can_access_job_projects', 'can_access_jobs'] as AppCapability[]
const PROJECTS_WRITE_CAPABILITIES = ['can_manage_projects', 'can_create_projects', 'can_access_projects'] as AppCapability[]
const EVENTS_CAPABILITIES = ['can_access_events', 'can_access_jobs'] as AppCapability[]
const EVENTS_WRITE_CAPABILITIES = ['can_manage_events', 'can_create_events', 'can_access_events', 'can_access_jobs'] as AppCapability[]
const JOB_CUSTOMERS_CAPABILITIES = ['can_access_job_customers'] as AppCapability[]
const WORKERS_CAPABILITIES = ['can_access_workers'] as AppCapability[]
const WORKER_HOTLINE_CAPABILITIES = ['can_access_worker_hotline'] as AppCapability[]
const VEHICLES_CAPABILITIES = ['can_access_vehicles'] as AppCapability[]
const ASSETS_CAPABILITIES = [
  'can_access_assets',
  'can_access_job_management',
  'can_access_jobs',
] as AppCapability[]
const MAINTENANCE_BOARD_CAPABILITIES = [
  'can_access_maintenance_board',
  'can_access_assets',
  'can_access_job_management',
  'can_access_jobs',
] as AppCapability[]
const JOB_LOCATIONS_CAPABILITIES = ['can_access_job_locations'] as AppCapability[]
const ASSIGNMENTS_CAPABILITIES = ['can_access_assignments'] as AppCapability[]
const WORKER_SKILLS_CAPABILITIES = ['can_access_worker_skills'] as AppCapability[]
const TEAMS_CAPABILITIES = ['can_access_teams'] as AppCapability[]
const WORKDAYS_CAPABILITIES = ['can_access_workdays'] as AppCapability[]
const JOB_CONFIGURATION_CAPABILITIES = ['can_access_job_configuration'] as AppCapability[]
const JOB_WORKFLOWS_CAPABILITIES = ['can_access_job_workflows'] as AppCapability[]
const JOB_STATUS_TRANSITIONS_CAPABILITIES = ['can_access_job_status_transitions'] as AppCapability[]
const FINANCE_CAPABILITIES = ['can_access_finance'] as AppCapability[]
const FINANCE_REPORTS_CAPABILITIES = ['can_access_finance_reports'] as AppCapability[]
const MONTHWISE_REPORTS_CAPABILITIES = ['can_access_monthwise_reports'] as AppCapability[]
const COMPANY_REPORTS_CAPABILITIES = ['can_access_company_reports'] as AppCapability[]
const WORKER_REPORTS_CAPABILITIES = ['can_access_worker_reports'] as AppCapability[]
const CLIENT_REPORTS_CAPABILITIES = ['can_access_client_reports'] as AppCapability[]
const EXPENSES_CAPABILITIES = ['can_access_expenses'] as AppCapability[]
const INVOICES_CAPABILITIES = ['can_access_invoices'] as AppCapability[]
const INVOICE_PAYMENTS_CAPABILITIES = ['can_access_invoice_payments'] as AppCapability[]
const INVENTORY_CAPABILITIES = ['can_access_inventory'] as AppCapability[]
const WORKER_INVOICES_CAPABILITIES = ['can_access_worker_invoices'] as AppCapability[]
const WORKER_PAYOUTS_CAPABILITIES = ['can_access_worker_payouts'] as AppCapability[]
const COMPANY_PAYMENTS_CAPABILITIES = ['can_access_company_payments'] as AppCapability[]
const INTER_COMPANY_SETTLEMENT_CAPABILITIES = ['can_access_inter_company_settlement'] as AppCapability[]

export const TENANT_CAPABILITIES: AppCapability[] = [
  'can_view_tenant_directory',
  'can_access_tenant_directory',
  'can_create_tenants',
  'can_grant_tenant_role_access',
  'can_revoke_tenant_role_access',
  'can_change_tenant_super_admin',
]

const ORGANIZATION_SETUP_CAPABILITIES: AppCapability[] = [
  'can_view_organization_tree',
  'can_access_organization_workspace',
  'can_access_legacy_node_pages',
  'can_access_legacy_node_settings',
]

const SERVICE_INSTANCE_FORM_CAPABILITIES: AppCapability[] = [
  ...ORGANIZATION_SETUP_CAPABILITIES,
  ...BRANCH_CAPABILITIES,
]

const ROUTE_ACCESS_DEFINITIONS: RouteAccessDefinition[] = [
  { path: '/posts', feature: 'Role Selection', allowedScopes: ['shared'] },
  { path: '/profile', feature: 'Profile', allowedScopes: ['shared'] },
  { path: '/dashboard', feature: 'Dashboard', allowedScopes: ['shared'] },
  { path: '/org', feature: 'Organization Setup', allowedScopes: ['organization', 'service'], anyCapabilities: ORGANIZATION_SETUP_CAPABILITIES },
  { path: '/org/service-instrance/form', feature: 'Organization Setup', allowedScopes: ['organization', 'service'], anyCapabilities: SERVICE_INSTANCE_FORM_CAPABILITIES },
  { path: '/organization/service-instrance/form', feature: 'Organization Setup', allowedScopes: ['organization', 'service'], anyCapabilities: SERVICE_INSTANCE_FORM_CAPABILITIES },
  { path: '/org/service-instance', feature: 'Branch Directory', allowedScopes: ['organization', 'service'], anyCapabilities: BRANCH_CAPABILITIES },
    {
      path: '/org/service-instance/form',
      feature: 'Organization Setup',
      allowedScopes: ['organization', 'service'],
      anyCapabilities: SERVICE_INSTANCE_FORM_CAPABILITIES,
    },
    {
      path: '/organization/service-instance/form',
      feature: 'Organization Setup',
      allowedScopes: ['organization', 'service'],
      anyCapabilities: SERVICE_INSTANCE_FORM_CAPABILITIES,
    },
  { path: '/org/tenants', feature: 'Tenant Directory', allowedScopes: ['organization', 'service'], anyCapabilities: TENANT_CAPABILITIES },
  { path: '/org/tree', feature: 'Organization Setup', allowedScopes: ['organization', 'service'], anyCapabilities: ORGANIZATION_SETUP_CAPABILITIES },
  {
    path: '/organization/node/service-instrance/media-form',
    feature: 'Branch',
    allowedScopes: ['organization', 'service'],
    anyCapabilities: BRANCH_CAPABILITIES,
  },
  {
    path: '/organization/node/branch-instrance/media-form',
    feature: 'Branch',
    allowedScopes: ['organization', 'service'],
    anyCapabilities: BRANCH_CAPABILITIES,
  },
  { path: '/org/node/service-instrance/media-form', feature: 'Branch', allowedScopes: ['organization', 'service'], anyCapabilities: BRANCH_CAPABILITIES },
  { path: '/org/node/branch-instrance/media-form', feature: 'Branch', allowedScopes: ['organization', 'service'], anyCapabilities: BRANCH_CAPABILITIES },
  { path: '/org/organization', feature: 'Organization Setup', allowedScopes: ['organization', 'service'], anyCapabilities: ORGANIZATION_SETUP_CAPABILITIES },
  { path: '/org/branch', feature: 'Branch Workspace', allowedScopes: ['organization', 'service'], anyCapabilities: ORGANIZATION_SETUP_CAPABILITIES },
  { path: '/org/templates', feature: 'Templates', allowedScopes: ['organization', 'service'], anyCapabilities: TEMPLATES_CAPABILITIES },
  { path: '/org/in-apps', feature: 'In-Apps', allowedScopes: ['organization', 'service'], anyCapabilities: IN_APPS_CAPABILITIES },
  { path: '/org/features', feature: 'Features', allowedScopes: ['organization'], anyCapabilities: ['can_manage_roles'] },
  { path: '/org/catalog-roles', feature: 'Role skills', allowedScopes: ['organization', 'service'], anyCapabilities: ['can_manage_roles'] },
  { path: '/org/manage-roles', feature: 'Manage Roles', allowedScopes: ['organization', 'service'], anyCapabilities: ['can_manage_roles'] },
  { path: '/org/roles', feature: 'Role Assignments', allowedScopes: ['organization', 'service'], anyCapabilities: ROLE_DIRECTORY_CAPABILITIES },
  { path: '/org/users', feature: 'User Directory', allowedScopes: ['organization', 'service'], anyCapabilities: USER_DIRECTORY_CAPABILITIES },
  {
    path: '/org/verticals',
    feature: 'Verticals & levels',
    allowedScopes: ['organization', 'service'],
    anyCapabilities: ['can_access_organization_verticals'],
  },
  { path: '/job', feature: 'Jobs', allowedScopes: ['service'], anyCapabilities: JOBS_CAPABILITIES },
  { path: '/projects', feature: 'Projects', allowedScopes: ['service'], anyCapabilities: PROJECTS_CAPABILITIES },
  { path: '/projects/create', feature: 'Create Project', allowedScopes: ['service'], anyCapabilities: PROJECTS_WRITE_CAPABILITIES },
  { path: '/projects/new', feature: 'Create Project', allowedScopes: ['service'], anyCapabilities: PROJECTS_WRITE_CAPABILITIES },
  { path: '/projects/lifecycle', feature: 'Project Lifecycle', allowedScopes: ['service'], anyCapabilities: PROJECTS_CAPABILITIES },
  { path: '/projects/checklists', feature: 'Project Checklists', allowedScopes: ['service'], anyCapabilities: PROJECTS_CAPABILITIES },
  { path: '/projects/configuration', feature: 'Project Configuration', allowedScopes: ['service'], anyCapabilities: PROJECTS_CAPABILITIES },
  { path: '/projects/workflows', feature: 'Project Workflows', allowedScopes: ['service'], anyCapabilities: PROJECTS_CAPABILITIES },
  { path: '/projects/status-transitions', feature: 'Project Status Transitions', allowedScopes: ['service'], anyCapabilities: PROJECTS_CAPABILITIES },
  { path: '/project/locations', feature: 'Project Locations', allowedScopes: ['service'], anyCapabilities: JOB_LOCATIONS_CAPABILITIES },
  { path: '/projects/locations', feature: 'Project Locations', allowedScopes: ['service'], anyCapabilities: JOB_LOCATIONS_CAPABILITIES },
  { path: '/projects/customers', feature: 'Project Customers', allowedScopes: ['service'], anyCapabilities: PROJECTS_CAPABILITIES },
  { path: '/projects/assignments', feature: 'Project Assignments', allowedScopes: ['service'], anyCapabilities: PROJECTS_CAPABILITIES },
  { path: '/projects/workdays', feature: 'Project Work Days', allowedScopes: ['service'], anyCapabilities: PROJECTS_CAPABILITIES },
  { path: '/events', feature: 'Events', allowedScopes: ['service'], anyCapabilities: EVENTS_CAPABILITIES },
  { path: '/events/create', feature: 'Create Event', allowedScopes: ['service'], anyCapabilities: EVENTS_WRITE_CAPABILITIES },
  { path: '/events/new', feature: 'Create Event', allowedScopes: ['service'], anyCapabilities: EVENTS_WRITE_CAPABILITIES },
  { path: '/events/lifecycle', feature: 'Event Lifecycle', allowedScopes: ['service'], anyCapabilities: EVENTS_CAPABILITIES },
  { path: '/events/checklists', feature: 'Event Checklists', allowedScopes: ['service'], anyCapabilities: EVENTS_CAPABILITIES },
  { path: '/events/configuration', feature: 'Event Configuration', allowedScopes: ['service'], anyCapabilities: EVENTS_CAPABILITIES },
  { path: '/events/workflows', feature: 'Event Workflows', allowedScopes: ['service'], anyCapabilities: EVENTS_CAPABILITIES },
  { path: '/events/status-transitions', feature: 'Event Status Transitions', allowedScopes: ['service'], anyCapabilities: EVENTS_CAPABILITIES },
  { path: '/event/locations', feature: 'Event Locations', allowedScopes: ['service'], anyCapabilities: JOB_LOCATIONS_CAPABILITIES },
  { path: '/events/locations', feature: 'Event Locations', allowedScopes: ['service'], anyCapabilities: JOB_LOCATIONS_CAPABILITIES },
  { path: '/events/customers', feature: 'Event Customers', allowedScopes: ['service'], anyCapabilities: EVENTS_CAPABILITIES },
  { path: '/events/assignments', feature: 'Event Assignments', allowedScopes: ['service'], anyCapabilities: EVENTS_CAPABILITIES },
  { path: '/events/workdays', feature: 'Event Days', allowedScopes: ['service'], anyCapabilities: EVENTS_CAPABILITIES },
  { path: '/job/dashboard', feature: 'Job Management', allowedScopes: ['service'], anyCapabilities: JOB_MANAGEMENT_CAPABILITIES },
  { path: '/job/customers', feature: 'Job Customers', allowedScopes: ['service'], anyCapabilities: JOB_CUSTOMERS_CAPABILITIES },
  { path: '/job/locations', feature: 'Locations', allowedScopes: ['service'], anyCapabilities: JOB_LOCATIONS_CAPABILITIES },
  { path: '/live', feature: 'Live', allowedScopes: ['service'], anyCapabilities: JOB_LOCATIONS_CAPABILITIES },
  { path: '/job/live', feature: 'Live', allowedScopes: ['service'], anyCapabilities: JOB_LOCATIONS_CAPABILITIES },
  { path: '/job/configuration', feature: 'Configuration', allowedScopes: ['service'], anyCapabilities: JOB_CONFIGURATION_CAPABILITIES },
  { path: '/job/workflows', feature: 'Workflows', allowedScopes: ['service'], anyCapabilities: JOB_WORKFLOWS_CAPABILITIES },
  { path: '/job/status-transitions', feature: 'Status Transitions', allowedScopes: ['service'], anyCapabilities: JOB_STATUS_TRANSITIONS_CAPABILITIES },
  { path: '/categories', feature: 'Categories', allowedScopes: ['service'], anyCapabilities: JOB_CATEGORIES_CAPABILITIES },
  { path: '/tags', feature: 'Tags', allowedScopes: ['service'], anyCapabilities: JOB_CATEGORIES_CAPABILITIES },
  { path: '/workers', feature: 'Workers', allowedScopes: ['service'], anyCapabilities: WORKERS_CAPABILITIES },
  { path: '/job/workers', feature: 'Workers', allowedScopes: ['service'], anyCapabilities: WORKERS_CAPABILITIES },
  { path: '/event/crew', feature: 'Event Crew', allowedScopes: ['service'], anyCapabilities: WORKERS_CAPABILITIES },
  { path: '/events/crew', feature: 'Event Crew', allowedScopes: ['service'], anyCapabilities: WORKERS_CAPABILITIES },
  { path: '/workers/hotline-history', feature: 'Hotline', allowedScopes: ['service'], anyCapabilities: WORKER_HOTLINE_CAPABILITIES },
  { path: '/vehicles', feature: 'Vehicles', allowedScopes: ['service'], anyCapabilities: VEHICLES_CAPABILITIES },
  { path: '/assets', feature: 'Assets', allowedScopes: ['service'], anyCapabilities: ASSETS_CAPABILITIES },
  { path: '/assets/maintenance', feature: 'Maintenance', allowedScopes: ['service'], anyCapabilities: MAINTENANCE_BOARD_CAPABILITIES },
  { path: '/assets/configuration', feature: 'Asset settings', allowedScopes: ['service'], anyCapabilities: ASSETS_CAPABILITIES },
  { path: '/assets/reports', feature: 'Asset reports', allowedScopes: ['service'], anyCapabilities: ASSETS_CAPABILITIES },
  { path: '/assets/contracts', feature: 'Asset contracts', allowedScopes: ['service'], anyCapabilities: ASSETS_CAPABILITIES },
  { path: '/assets/lookup', feature: 'Asset lookup', allowedScopes: ['service'], anyCapabilities: ASSETS_CAPABILITIES },
  { path: '/assets/my', feature: 'My assets', allowedScopes: ['service'], anyCapabilities: ASSETS_CAPABILITIES },
  { path: '/assets/locations', feature: 'Asset locations', allowedScopes: ['service'], anyCapabilities: ASSETS_CAPABILITIES },
  { path: '/locations', feature: 'Locations', allowedScopes: ['service'], anyCapabilities: JOB_LOCATIONS_CAPABILITIES },
  { path: '/assignments', feature: 'Assignments', allowedScopes: ['service'], anyCapabilities: ASSIGNMENTS_CAPABILITIES },
  { path: '/workdays', feature: 'Work Days', allowedScopes: ['service'], anyCapabilities: WORKDAYS_CAPABILITIES },
  { path: '/timeline', feature: 'Timeline', allowedScopes: ['service'], anyCapabilities: WORKDAYS_CAPABILITIES },
  { path: '/reports/monthwise', feature: 'Monthwise Reports', allowedScopes: ['service'], anyCapabilities: MONTHWISE_REPORTS_CAPABILITIES },
  { path: '/reports/company', feature: 'Company Reports', allowedScopes: ['service'], anyCapabilities: COMPANY_REPORTS_CAPABILITIES },
  { path: '/reports/worker', feature: 'Worker Reports', allowedScopes: ['service'], anyCapabilities: WORKER_REPORTS_CAPABILITIES },
  { path: '/reports/client', feature: 'Client Reports', allowedScopes: ['service'], anyCapabilities: CLIENT_REPORTS_CAPABILITIES },
  { path: '/reports', feature: 'Reports', allowedScopes: ['service'], anyCapabilities: FINANCE_REPORTS_CAPABILITIES },
  { path: '/job/expenses', feature: 'Expenses', allowedScopes: ['service'], anyCapabilities: EXPENSES_CAPABILITIES },
  { path: '/job/particulars', feature: 'Inventory', allowedScopes: ['service'], anyCapabilities: INVENTORY_CAPABILITIES },
  { path: '/job/worker/invoices', feature: 'Worker Invoices', allowedScopes: ['service'], anyCapabilities: WORKER_INVOICES_CAPABILITIES },
  { path: '/job/worker/payments', feature: 'Worker Payouts', allowedScopes: ['service'], anyCapabilities: WORKER_PAYOUTS_CAPABILITIES },
  { path: '/job/company/payments', feature: 'Payments to Company', allowedScopes: ['service'], anyCapabilities: COMPANY_PAYMENTS_CAPABILITIES },
  { path: '/finance/invoices', feature: 'Invoices', allowedScopes: ['service'], anyCapabilities: INVOICES_CAPABILITIES },
  { path: '/finance/invoice-payments', feature: 'Invoice Payments', allowedScopes: ['service'], anyCapabilities: INVOICE_PAYMENTS_CAPABILITIES },
  { path: '/finance/settlement-invoices', feature: 'Inter-Company Settlement', allowedScopes: ['service'], anyCapabilities: INTER_COMPANY_SETTLEMENT_CAPABILITIES },
  { path: '/finance', feature: 'Finance', allowedScopes: ['service'], anyCapabilities: FINANCE_CAPABILITIES },
  { path: '/finance/taxes', feature: 'Taxes', allowedScopes: ['service'], anyCapabilities: FINANCE_CAPABILITIES },
  { path: '/services/dashboard', feature: 'Service Dashboard', allowedScopes: ['service'], anyCapabilities: SERVICE_DASHBOARD_CAPABILITIES },
  { path: '/membership/dashboard', feature: 'Membership Dashboard', allowedScopes: ['service'], anyCapabilities: MEMBERSHIP_CAPABILITIES },
  { path: '/sales/dashboard', feature: 'Sales & Enquiries', allowedScopes: ['service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/sales/enquiries', feature: 'Sales & Enquiries', allowedScopes: ['service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/sales/enquiries/:enquiryId/follow-ups', feature: 'Sales & Enquiries', allowedScopes: ['service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/sales/follow-ups', feature: 'Sales & Enquiries', allowedScopes: ['service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/services/pos', feature: 'New Booking', allowedScopes: ['service'], anyCapabilities: SERVICE_STORE_POS_CAPABILITIES },
  { path: '/services/products', feature: 'Services', allowedScopes: ['service'], anyCapabilities: SERVICE_STORE_PRODUCTS_CAPABILITIES },
  { path: '/services/worker-skills', feature: 'Worker Skills', allowedScopes: ['service'], anyCapabilities: WORKER_SKILLS_CAPABILITIES },
  { path: '/services/crews', feature: 'Teams', allowedScopes: ['service'], anyCapabilities: TEAMS_CAPABILITIES },
  { path: '/services/pay-scales', feature: 'PayScale', allowedScopes: ['service'], anyCapabilities: SERVICE_STORE_PRODUCTS_CAPABILITIES },
  { path: '/services/memberships', feature: 'Membership', allowedScopes: ['service'], anyCapabilities: MEMBERSHIP_CAPABILITIES },
  {
    path: '/services/product-variants',
    feature: 'Service Packages',
    allowedScopes: ['service'],
    anyCapabilities: ['can_access_product_variants', 'can_access_branch_service_products'],
  },
  { path: '/services/categories', feature: 'Service Categories', allowedScopes: ['organization', 'service'], anyCapabilities: SERVICE_STORE_CATEGORIES_CAPABILITIES },
  { path: '/services/quotations', feature: 'Quotations', allowedScopes: ['organization', 'service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/services/quotations/new', feature: 'Create Quotation', allowedScopes: ['organization', 'service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/services/quotations/new/pos', feature: 'Create Quotation (POS)', allowedScopes: ['organization', 'service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/services/orders', feature: 'Bookings', allowedScopes: ['organization', 'service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/services/bookings-dashboard', feature: 'Bookings Dashboard', allowedScopes: ['organization', 'service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/services/credits', feature: 'Credit', allowedScopes: ['organization', 'service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/services/order-payments', feature: 'Order Payments', allowedScopes: ['organization', 'service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/services/refunds', feature: 'Refunds', allowedScopes: ['organization', 'service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/services/refund-policies', feature: 'Refund Policies', allowedScopes: ['organization', 'service'], anyCapabilities: SERVICE_STORE_ORDERS_CAPABILITIES },
  { path: '/services/whatsapp', feature: 'Communication', allowedScopes: ['service'], anyCapabilities: COMMUNICATION_CAPABILITIES },
  { path: '/marketing/announcements', feature: 'Announcements', allowedScopes: ['organization', 'service'], anyCapabilities: ANNOUNCEMENTS_CAPABILITIES },
  { path: '/marketing/announcements/create', feature: 'Announcements', allowedScopes: ['organization', 'service'], anyCapabilities: ANNOUNCEMENTS_CAPABILITIES },
  { path: '/marketing/announcements/:id/edit', feature: 'Announcements', allowedScopes: ['organization', 'service'], anyCapabilities: ANNOUNCEMENTS_CAPABILITIES },
  {
    path: '/services/customers',
    feature: 'Customers',
    allowedScopes: ['service'],
    anyCapabilities: CUSTOMER_DIRECTORY_CAPABILITIES,
  },
  { path: '/services/offers', feature: 'Offers', allowedScopes: ['service'], anyCapabilities: SERVICE_STORE_OFFERS_CAPABILITIES },
  { path: '/planner', feature: 'Resource Planner', allowedScopes: ['service'], anyCapabilities: RESOURCE_PLANNER_CAPABILITIES },
]

function getRouteScopeLabel(scopes: RouteScope[]) {
  if (scopes.length === 1 && scopes[0] === 'organization') {
    return 'Organization'
  }

  if (scopes.length === 1 && scopes[0] === 'service') {
    return 'Service Branch'
  }

  return 'Current role scope'
}

const asTruthyCapabilityFlag = (value: unknown) => {
  if (value === true || value === 1) {
    return true
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'enabled'
  }

  return false
}

const capabilityObjectHasAnyTruthyFlag = (value: unknown) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  return Object.values(value as Record<string, unknown>).some((entry) => asTruthyCapabilityFlag(entry))
}

export function postHasAnyCapabilities(post: Post | null | undefined, capabilities: AppCapability[] | undefined) {
  if (!capabilities || capabilities.length === 0) {
    return true
  }

  if (capabilities.some((capability) => isCapabilityBlockedBySectionParent(capability, post?.capabilities))) {
    return false
  }

  if (capabilities.includes('can_access_job_customers') && post?.capabilities?.can_view_customer_directory === false) {
    return false
  }

  const rolePermissions = post?.roles_data?.permissions
  const hasCapabilityLikeObject = (value: unknown) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false
    }

    return Object.values(value as Record<string, unknown>).some((entry) => {
      if (entry === null || entry === undefined) {
        return false
      }
      const valueType = typeof entry
      return valueType === 'boolean' || valueType === 'number' || valueType === 'string'
    })
  }

  const hasCapabilitiesObject = hasCapabilityLikeObject(post?.capabilities)
  const hasCapabilityLikeRolePermissions = Boolean(
    hasCapabilityLikeObject(rolePermissions),
  )

  // Avoid hard-locking navigation while capability payload is still hydrating.
  // Backend authorization still enforces access server-side.
  if (!hasCapabilitiesObject && !hasCapabilityLikeRolePermissions) {
    if (STRICT_CAPABILITY_GUARDS) {
      return false
    }
    return true
  }

  const hasAnyEnabledCapability = capabilityObjectHasAnyTruthyFlag(post?.capabilities)
    || capabilityObjectHasAnyTruthyFlag(rolePermissions)

  const postCapabilities = (post?.capabilities ?? undefined) as Partial<Record<AppCapability, boolean>> | undefined

  // New features often return an explicit all-false capability map until RolePermissions sync.
  if (!hasAnyEnabledCapability && !STRICT_CAPABILITY_GUARDS) {
    const requestedExplicitTrue = capabilities.some((capability) => postCapabilities?.[capability] === true)
    const requestedExplicitFalse = capabilities.some((capability) => postCapabilities?.[capability] === false)
    if (!(requestedExplicitFalse && !requestedExplicitTrue)) {
      return true
    }
  }

  const readCapabilityFlag = (capability: AppCapability) => {
    const capabilityValue = postCapabilities?.[capability]
    if (capabilityValue === false) {
      return false
    }
    if (isCapabilityBlockedBySectionParent(capability, postCapabilities)) {
      return false
    }
    if (capability === 'can_access_job_customers' && postCapabilities?.can_view_customer_directory === false) {
      return false
    }
    if (capabilityValue === true) {
      return true
    }

    if (!rolePermissions || typeof rolePermissions !== 'object') {
      return false
    }

    const permissionValue = (rolePermissions as Record<string, unknown>)[capability]
    return permissionValue === true || permissionValue === 1 || String(permissionValue).toLowerCase() === 'true'
  }

  return capabilities.some((capability) => readCapabilityFlag(capability))
}

function resolveCurrentRouteScope(post: Post | null | undefined): RouteScope {
  let { uniqueId, label } = readServiceVariantIdentity(post)
  if (!uniqueId && !label) {
    uniqueId = String(post?.roles_data?.level_variant_unique_id ?? '').trim()
    label = String(
      post?.roles_data?.level_variant_name
      ?? post?.roles_data?.tier_code
      ?? '',
    ).trim()
  }

  if (isOrganizationServiceVariant(uniqueId, label)) {
    return 'organization'
  }

  return 'service'
}

function findRouteDefinition(pathname: string) {
  return ROUTE_ACCESS_DEFINITIONS
    .slice()
    .sort((left, right) => right.path.length - left.path.length)
    .find((definition) => pathname === definition.path || pathname.startsWith(`${definition.path}/`))
}

function resolveRouteFeatureLabel(definition: RouteAccessDefinition, post: Post | null | undefined) {
  if (definition.path === '/org' || definition.path === '/org/tree' || definition.path === '/org/organization' || definition.path === '/org/branch') {
    return getOrganizationTreeSetupLabel(
      post?.service_variant_unique_id,
      post?.service_variant_name ?? post?.service_variant_type,
    )
  }

  return definition.feature
}

export function getCurrentRoleScopeLabel(post: Post | null | undefined) {
  const serviceVariantUniqueId = String(post?.service_variant_unique_id ?? '').trim()
  const serviceVariantLabel = String(post?.service_variant_name ?? post?.service_variant_type ?? '').trim()
  const variantLabel = getCanonicalServiceVariantLabel(
    serviceVariantUniqueId || post?.roles_data?.level_variant_unique_id,
    serviceVariantLabel || post?.roles_data?.level_variant_name || post?.roles_data?.tier_code,
  )

  return variantLabel || 'Unknown'
}

/** Branch directory / management capabilities required for media uploads and slot configuration. */
export function hasBranchMediaCapability(post: Post | null | undefined): boolean {
  if (postHasAnyCapabilities(post, BRANCH_CAPABILITIES)) {
    return true
  }

  const rolePermissions = post?.roles_data?.permissions
  if (!rolePermissions || typeof rolePermissions !== 'object') {
    return false
  }

  return BRANCH_MEDIA_PERMISSION_ACTIONS.some((action) => {
    const value = (rolePermissions as Record<string, unknown>)[action]
    return value === true || value === 1 || String(value).toLowerCase() === 'true'
  })
}

function resolvePostForRouteCapabilityChecks(post: Post | null | undefined): Post | null | undefined {
  if (!post) {
    return post
  }

  const capabilities = withSystemRoleCapabilityFallback(post, post.capabilities)
  if (!capabilities || capabilities === post.capabilities) {
    return post
  }

  return {
    ...post,
    capabilities: capabilities as NonNullable<Post['capabilities']>,
  }
}

export function evaluateRouteAccess(pathname: string, post: Post | null | undefined): RouteAccessDecision {
  const definition = findRouteDefinition(pathname)
  if (!definition) {
    return {
      authorized: true,
      feature: null,
      requiredScopeLabel: null,
      reason: null,
    }
  }

  const featureLabel = resolveRouteFeatureLabel(definition, post)

  if (definition.allowedScopes.includes('shared')) {
    return {
      authorized: true,
      feature: featureLabel,
      requiredScopeLabel: getRouteScopeLabel(definition.allowedScopes),
      reason: null,
    }
  }

  const currentScope = resolveCurrentRouteScope(post)
  if (!definition.allowedScopes.includes(currentScope)) {
    return {
      authorized: false,
      feature: featureLabel,
      requiredScopeLabel: getRouteScopeLabel(definition.allowedScopes),
      reason: 'scope',
    }
  }

  const routePath = definition.path
  const isMembershipRoute = routePath === '/services/memberships' || routePath === '/membership/dashboard'
  const isProjectRoute = routePath === '/projects' || routePath.startsWith('/projects/')
  const isEventRoute = routePath === '/events' || routePath.startsWith('/events/')

  if (
    (isMembershipRoute && MEMBERSHIP_FEATURE_ENABLED)
    || (isProjectRoute && PROJECT_MGMT_FEATURE_ENABLED)
    || (isEventRoute && EVENT_MGMT_FEATURE_ENABLED)
  ) {
    return {
      authorized: true,
      feature: featureLabel,
      requiredScopeLabel: getRouteScopeLabel(definition.allowedScopes),
      reason: null,
    }
  }

  if (!postHasAnyCapabilities(resolvePostForRouteCapabilityChecks(post), definition.anyCapabilities)) {
    return {
      authorized: false,
      feature: featureLabel,
      requiredScopeLabel: getRouteScopeLabel(definition.allowedScopes),
      reason: 'capability',
    }
  }

  return {
    authorized: true,
    feature: featureLabel,
    requiredScopeLabel: getRouteScopeLabel(definition.allowedScopes),
    reason: null,
  }
}